import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const NEWSLETTER_FORM_URL = 'https://readdy.ai/api/form/d9q1dp6e3oq4jqumtf6g';

export default function CTASection() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMsg('');

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setStatus('success');
      setEmail('');
      return;
    }

    try {
      const res = await fetch(NEWSLETTER_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ email: formData.get('email') as string }).toString(),
      });
      const text = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(text); } catch { /* ignore */ }

      if (res.ok && parsed?.code === 'OK') {
        setStatus('success');
        setEmail('');
      } else {
        const msg = parsed?.meta?.message || parsed?.message || text || '';
        if (msg.toLowerCase().includes('spam') || msg.includes('spam')) {
          setStatus('error');
          setErrorMsg(t('newsletter.error'));
        } else {
          setStatus('error');
          setErrorMsg(msg || t('newsletter.error'));
        }
      }
    } catch {
      setStatus('error');
      setErrorMsg(t('newsletter.error'));
    }
  };

  return (
    <section className="relative py-16 md:py-20 overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url(https://readdy.ai/api/search-image?query=Warm%20cinematic%20abstract%20background%20with%20flowing%20gradients%20in%20deep%20orange%20and%20charcoal%20tones%20soft%20bokeh%20lights%20elegant%20movie%20theater%20atmosphere%20dark%20rich%20textures%20subtle%20glow%20effects&width=1920&height=600&seq=cta-bg-1&orientation=landscape)`,
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/70" />

      <div className="relative z-10 w-full px-4 md:px-6">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-background-50 mb-3">
            {t('cta.title')}
          </h2>
          <p className="text-sm md:text-base text-background-50/75 mb-6">
            {t('cta.subtitle')}
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-all hover:scale-105 whitespace-nowrap"
          >
            {t('cta.button')}
            <i className="ri-arrow-right-line"></i>
          </Link>

          <div className="mt-10 pt-8 border-t border-background-50/15">
            <h3 className="text-base font-semibold text-background-50 mb-2">{t('newsletter.title')}</h3>
            <p className="text-xs text-background-50/60 mb-4">{t('newsletter.subtitle')}</p>
            <form
              onSubmit={handleNewsletterSubmit}
              data-readdy-form
              className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
            >
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="absolute opacity-0 pointer-events-none"
              />
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('newsletter.placeholder')}
                required
                className="flex-1 px-4 py-2.5 rounded-full bg-background-50/10 border border-background-50/20 text-background-50 placeholder:text-background-50/40 text-sm focus:outline-none focus:border-background-50/50"
              />
              <button
                type="submit"
                className="px-6 py-2.5 rounded-full bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors whitespace-nowrap"
              >
                {t('newsletter.button')}
              </button>
            </form>
            {status === 'success' && (
              <p className="text-xs text-emerald-400 mt-3">{t('newsletter.success')}</p>
            )}
            {status === 'error' && (
              <p className="text-xs text-red-400 mt-3">{errorMsg}</p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}